import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const CONFIGS = {
  posts: {
    endpoint: "/admin/posts",
    singular: "reflexão",
    listTitle: (item) => item.title,
    listMeta: (item) => (item.published ? "Publicada" : "Rascunho"),
    empty: { title: "", excerpt: "", content: "", cover_url: "", published: true },
    fields: [
      { name: "title", label: "Título" },
      { name: "excerpt", label: "Resumo", textarea: true },
      { name: "content", label: "Conteúdo (parágrafos separados por linha em branco)", textarea: true, rows: 8 },
      { name: "cover_url", label: "Imagem de capa", upload: true },
      { name: "published", label: "Publicada", checkbox: true },
    ],
  },
  products: {
    endpoint: "/admin/products",
    singular: "produto",
    listTitle: (item) => item.name,
    listMeta: (item) => item.price_label,
    empty: { name: "", description: "", price_label: "", image_url: "", checkout_url: "", featured: false },
    fields: [
      { name: "name", label: "Nome" },
      { name: "description", label: "Descrição", textarea: true, rows: 4 },
      { name: "price_label", label: "Preço (ex: R$ 97/mês)" },
      { name: "image_url", label: "Imagem do produto", upload: true },
      { name: "checkout_url", label: "Link externo de compra (Hotmart, Kiwify…)" },
    ],
  },
  mentoria: {
    endpoint: "/admin/mentoria",
    singular: "conteúdo",
    listTitle: (item) => item.title,
    listMeta: (item) => item.tag,
    empty: { title: "", description: "", url: "", tag: "Conteúdo", order: 0 },
    fields: [
      { name: "title", label: "Título" },
      { name: "description", label: "Descrição", textarea: true, rows: 4 },
      { name: "url", label: "URL do conteúdo (vídeo, PDF, link)" },
      { name: "tag", label: "Etiqueta (ex: Vídeo, PDF, Ao vivo)" },
      { name: "order", label: "Ordem", number: true },
    ],
  },
};

export default function AdminCollections({ kind }) {
  const config = CONFIGS[kind];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get(config.endpoint).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, [kind]); // eslint-disable-line

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`${config.endpoint}/${editing.id}`, editing);
        toast.success("Alterações salvas.");
      } else {
        await api.post(config.endpoint, editing);
        toast.success("Criado com sucesso.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Excluir "${config.listTitle(item)}"?`)) return;
    try {
      await api.delete(`${config.endpoint}/${item.id}`);
      toast.success("Excluído.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setField = (name, value) => setEditing({ ...editing, [name]: value });

  if (loading) return <p className="font-serif text-zinc-500">Carregando…</p>;

  return (
    <div data-testid={`admin-collection-${kind}`}>
      {!editing ? (
        <>
          <button onClick={() => setEditing({ ...config.empty })} className="btn-gold mb-8" data-testid={`new-${kind}-button`}>
            <Plus size={14} /> Nova {config.singular}
          </button>
          <div className="flex flex-col gap-px bg-gold/10">
            {items.length === 0 && <p className="bg-[#0a0a0a] p-8 text-sm text-zinc-500">Nenhum item ainda.</p>}
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-4 bg-[#0a0a0a] px-6 py-5" data-testid={`${kind}-row-${item.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-serif text-lg text-zinc-100">{config.listTitle(item)}</p>
                  <p className="mt-1 text-[0.65rem] uppercase tracking-[0.2em] text-gold/70">{config.listMeta(item)}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button onClick={() => setEditing(item)} className="border border-gold/25 p-2.5 text-gold transition-colors hover:border-gold/60" data-testid={`edit-${kind}-${item.id}`} aria-label="Editar">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(item)} className="border border-red-900/50 p-2.5 text-red-400 transition-colors hover:border-red-500" data-testid={`delete-${kind}-${item.id}`} aria-label="Excluir">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="max-w-3xl border border-gold/15 bg-[#0a0a0a] p-8" data-testid={`${kind}-editor`}>
          <div className="mb-8 flex items-center justify-between">
            <h3 className="font-serif text-xl text-zinc-100">{editing.id ? "Editar" : "Nova"} {config.singular}</h3>
            <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-gold" data-testid={`${kind}-editor-close`} aria-label="Fechar">
              <X size={18} />
            </button>
          </div>
          <div className="flex flex-col gap-5">
            {config.fields.map((f) => (
              <div key={f.name}>
                {f.checkbox ? (
                  <label className="flex items-center gap-3 text-sm text-zinc-300">
                    <input type="checkbox" checked={!!editing[f.name]} onChange={(e) => setField(f.name, e.target.checked)} className="h-4 w-4 accent-[#D4AF37]" data-testid={`${kind}-field-${f.name}`} />
                    {f.label}
                  </label>
                ) : (
                  <>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-gold">{f.label}</label>
                    {f.textarea ? (
                      <textarea rows={f.rows || 3} value={editing[f.name] ?? ""} onChange={(e) => setField(f.name, e.target.value)} className="input-dark resize-none" data-testid={`${kind}-field-${f.name}`} />
                    ) : (
                      <input type={f.number ? "number" : "text"} value={editing[f.name] ?? ""} onChange={(e) => setField(f.name, f.number ? Number(e.target.value) : e.target.value)} className="input-dark" data-testid={`${kind}-field-${f.name}`} />
                    )}
                    {f.upload && (
                      <div className="mt-3 flex items-center gap-4">
                        <label className="btn-ghost-dark cursor-pointer !px-4 !py-2" data-testid={`${kind}-upload-${f.name}`}>
                          Enviar imagem do computador
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const fd = new FormData();
                              fd.append("file", file);
                              try {
                                const { data } = await api.post("/admin/upload", fd);
                                setField(f.name, data.url);
                                toast.success("Imagem enviada.");
                              } catch (err) {
                                toast.error(formatApiError(err));
                              }
                              e.target.value = "";
                            }}
                          />
                        </label>
                        {editing[f.name] && (
                          <img src={editing[f.name]} alt="Pré-visualização" className="h-14 w-14 border border-gold/25 object-cover" data-testid={`${kind}-preview-${f.name}`} />
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            <button onClick={save} className="btn-gold w-fit" data-testid={`${kind}-save-button`}>
              Salvar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
